import { Container } from '@resolute/ui';
import { Link } from 'react-router-dom';

import { Logo } from './logo';

import { type SocialPlatform, SocialIcon } from '@/components/social-icon';
import { CONTACT_DETAILS } from '@/features/contact/contact-details';

/** Footer social links — every platform in the shared contact details
 *  (single source of truth for hrefs), rendered with the shared, brand-
 *  accurate `SocialIcon` glyphs. */
const SOCIAL_ORDER: SocialPlatform[] = ['Facebook', 'Instagram', 'LinkedIn', 'YouTube'];

const SOCIAL_LINKS = SOCIAL_ORDER.map((platform) => ({
  platform,
  href: CONTACT_DETAILS.social.find((x) => x.label === platform)?.href ?? '#',
}));

const COLUMNS = [
  {
    title: 'Explore',
    links: [
      { label: 'Buy', to: '/buy' },
      { label: 'Rent', to: '/rent' },
      { label: 'New Inventory', to: '/inventory' },
      { label: 'Commercial', to: '/commercial' },
      { label: 'Pre-Leased', to: '/invest' },
      { label: 'GIFT City', to: '/gift-city' },
    ],
  },
  {
    // Labels and paths match `SIMPLE_NAV_ITEMS` in nav-menu.ts, so the header
    // dropdown and the footer never drift apart.
    title: 'Services',
    links: [
      { label: 'Group Booking', to: '/services/group-booking' },
      { label: 'BTS (Built-to-Suit)', to: '/services/built-to-suit' },
      { label: 'Investment Advisory', to: '/services/investment-advisory' },
      { label: 'Mandate', to: '/services/mandate' },
    ],
  },
  {
    title: 'Tools',
    links: [
      { label: 'EMI Calculator', to: '/calculators/emi' },
      { label: 'Affordability', to: '/calculators/affordability' },
      { label: 'Rental Yield', to: '/calculators/rental-yield' },
      { label: 'Stamp Duty', to: '/calculators/stamp-duty' },
      { label: 'Vaastu Tips', to: '/vaastu-tips' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Leadership', to: '/about/leadership' },
      { label: 'Contact', to: '/contact' },
      { label: 'Careers', to: '/careers' },
      { label: 'FAQs', to: '/faq' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy', to: '/privacy' },
      { label: 'Terms', to: '/terms' },
      { label: 'RERA Disclosures', to: '/rera' },
      { label: 'Cookies', to: '/cookies' },
    ],
  },
];

export function Footer() {
  return (
    <footer className="border-border bg-muted/40 border-t">
      <Container className="py-7 sm:py-8">
        {/* 7 tracks at lg: the brand block spans 2, then one per link column.
            Bump this alongside COLUMNS — a mismatch silently wraps the last
            column onto its own row. */}
        <div className="grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-7">
          {/* Full row until `lg`, where it sits alongside the link columns.
              At `md` the grid is 3 tracks: spanning only 2 left a single
              orphaned column stranded beside the brand block, and pushed the
              last of the five onto a row of its own. */}
          <div className="col-span-2 flex flex-col gap-4 md:col-span-3 lg:col-span-2">
            <Logo />
            <p className="text-muted-foreground max-w-xs text-sm">
              Verified, RERA-transparent inventory and investment-grade intelligence for
              India&apos;s growth corridors.
            </p>
            <ul className="mt-1 flex items-center gap-4">
              {SOCIAL_LINKS.map(({ platform, href }) => (
                <li key={platform}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Resolute Property Solutions on ${platform}`}
                    className="duration-fast ease-out-expo inline-flex p-1 transition-transform hover:-translate-y-0.5"
                  >
                    <SocialIcon platform={platform} className="size-6" />
                  </a>
                </li>
              ))}
            </ul>
            <p className="text-muted-foreground text-xs">
              RERA Reg. No.:
              <br />
              <span className="sm:whitespace-nowrap">{CONTACT_DETAILS.rera.number}</span>
            </p>
          </div>
          {COLUMNS.map((col) => (
            <nav key={col.title} aria-label={col.title} className="flex flex-col gap-3">
              <h2 className="text-foreground text-sm font-semibold">{col.title}</h2>
              <ul className="flex flex-col gap-2">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      // `block py-1 -my-1` grows the tap target from 17px to
                      // 25px tall and the full column width, while the negative
                      // margin cancels the padding's effect on layout — same
                      // pixels on screen, a target that clears the 24px
                      // minimum on a phone.
                      className="text-muted-foreground duration-fast ease-out-expo hover:text-foreground block -my-1 py-1 text-sm transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="border-border text-muted-foreground mt-10 flex flex-col items-start justify-between gap-4 border-t pt-6 text-xs sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} Resolute Property Solutions. All rights reserved.</p>
          <p>This site does not provide legal advice.</p>
        </div>
      </Container>
    </footer>
  );
}
